/* ============================================
   NOT FOUND PAGE
   The Creative Orchestra - GEMINI Framework
   404 error page
   ============================================ */

import { useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Home, ArrowLeft, Search, BookOpen, Sparkles } from 'lucide-react';
import { SITE_INFO } from '@utils/constants';

/**
 * Not Found Page Component
 * Displayed when user navigates to a non-existent route
 */
const NotFoundPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Set page title
  useEffect(() => {
    document.title = `Page Not Found | ${SITE_INFO.title}`;
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-20">
      <motion.div
        className="text-center max-w-lg"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* 404 Number */}
        <motion.div
          className="relative mb-8"
          initial={{ scale: 0.8 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', duration: 0.6 }}
        >
          <h1 
            className="text-[150px] md:text-[200px] font-display font-bold leading-none
              text-transparent bg-clip-text"
            style={{
              backgroundImage: 'linear-gradient(135deg, rgba(129, 140, 248, 0.3) 0%, rgba(168, 85, 247, 0.2) 100%)',
            }}
          >
            404
          </h1>
          
          {/* Decorative elements */}
          <motion.div
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2
              w-32 h-32 rounded-full"
            style={{
              background: 'radial-gradient(circle, rgba(129, 140, 248, 0.1) 0%, transparent 70%)',
            }}
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.5, 0.8, 0.5],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />
          
          {/* Floating sparkles */}
          <motion.div
            className="absolute top-4 right-8"
            animate={{ y: [0, -10, 0], rotate: [0, 180, 360] }}
            transition={{ duration: 4, repeat: Infinity }}
          >
            <Sparkles className="w-6 h-6 text-indigo-400/50" />
          </motion.div>
          <motion.div
            className="absolute bottom-8 left-12"
            animate={{ y: [0, 10, 0], rotate: [360, 180, 0] }}
            transition={{ duration: 5, repeat: Infinity }}
          >
            <Sparkles className="w-4 h-4 text-purple-400/50" />
          </motion.div>
        </motion.div>

        {/* Message */}
        <h2 className="text-2xl md:text-3xl font-display font-bold text-white mb-4">
          Lost in the Cosmos
        </h2>
        
        <p className="text-gray-400 mb-2">
          The page you're looking for doesn't exist or has been moved.
        </p>
        
        <p className="text-sm text-gray-600 mb-8 font-mono">
          {location.pathname}
        </p>

        {/* Action buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
          <motion.button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 px-5 py-2.5 rounded-lg
              bg-white/10 border border-white/10 text-white
              hover:bg-white/20 hover:border-white/20 transition-colors"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <ArrowLeft className="w-4 h-4" />
            Go Back
          </motion.button>

          <Link to="/">
            <motion.button
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg
                bg-indigo-500 text-white hover:bg-indigo-400 transition-colors"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Home className="w-4 h-4" />
              Return Home
            </motion.button>
          </Link>
        </div>

        {/* Quick links */}
        <div className="pt-8 border-t border-white/10">
          <p className="text-sm text-gray-500 mb-4">
            Here are some helpful links:
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <QuickLink to="/" icon={<Home className="w-4 h-4" />} label="Home" />
            <QuickLink to="/novel" icon={<BookOpen className="w-4 h-4" />} label="Novel" />
            <QuickLink to="/about" icon={<Sparkles className="w-4 h-4" />} label="About" />
            <QuickLink to="/work/genie" label="GENIE" />
            <QuickLink to="/work/midas" label="MIDAS" />
            <QuickLink to="/work/fenso" label="FENSO" />
          </div>
        </div>
      </motion.div>
    </div>
  );
};

/**
 * Quick Link Component
 */
const QuickLink = ({ to, icon, label }) => {
  return (
    <Link to={to}>
      <motion.span
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg
          bg-white/5 text-gray-400 text-sm
          hover:bg-white/10 hover:text-white transition-colors"
        whileHover={{ scale: 1.05 }}
      >
        {icon}
        {label}
      </motion.span>
    </Link>
  );
};

export default NotFoundPage;
